<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow menu-light"
  >
    <!-- main menu header-->
    <div class="navbar-header">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
      >
        <ul class="nav navbar-nav flex-row navbar-header-content">
          <!-- Logo -->
          <li class="nav-item nav-logo">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo brand-logo-full">
                <logo-full />
              </span>
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- Sidebar body wrapper for flex layout -->
    <div class="sidebar-body">
      <!-- main menu content-->
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="main-menu-content scroll-area"
        tagname="ul"
        @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
      >
        <vertical-nav-menu-items
          :items="navItems"
          class="navigation navigation-main"
        />
      </vue-perfect-scrollbar>
      <!-- /main menu content-->

      <!-- Sidebar footer -->
      <div class="sidebar-footer">
        <!-- User actions section -->
        <div class="sidebar-user-actions">
          <router-link
            :to="{ name: 'profile' }"
            class="user-action-link"
          >
            <feather-icon
              icon="UserIcon"
              size="18"
            />
            <span>{{ $t('DEBTIA_MY_PROFILE') }}</span>
          </router-link>
          <a
            href="#"
            class="user-action-link"
            @click.prevent="toggleLocale"
          >
            <b-img
              :src="currentLocale.img"
              height="14"
              width="22"
              class="locale-flag"
            />
            <span>{{ currentLocale.name }}</span>
          </a>
          <a
            href="#"
            class="user-action-link"
            @click.prevent="logout"
          >
            <feather-icon
              icon="LogOutIcon"
              size="18"
            />
            <span>{{ $t('DEBTIA_LOG_OUT') }}</span>
          </a>
        </div>

        <!-- Contact info box -->
        <div class="sidebar-contact-box">
          <div class="contact-title">
            {{ $t('DEBTIA_SIDEBAR_HELP') }}
          </div>
          <p class="contact-text">
            {{ $t('DEBTIA_SIDEBAR_CONTACT_TEXT') }}
          </p>
          <div class="contact-info">
            <a
              href="tel:+4544224000"
              class="contact-link"
            >
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
              <span>+45 44 22 40 00</span>
            </a>
            <a
              href="mailto:info@debtia.dk"
              class="contact-link"
            >
              <feather-icon
                icon="MailIcon"
                size="14"
              />
              <span>info@debtia.dk</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, ref } from '@vue/composition-api'
import { $themeConfig } from '@themeConfig'
import { initialAbility } from '@/libs/acl/config'
import useJwt from '@/auth/jwt/useJwt'
import LogoFull from '@core/layouts/components/LogoFull.vue'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    LogoFull,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      navItems: [],
    }
  },
  setup() {
    // Shadow bottom is UI specific
    const shallShadowBottom = ref(false)

    // Provide false for isMouseHovered (no longer used but may be expected by children)
    provide('isMouseHovered', ref(false))

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    /* eslint-disable global-require */
    const locales = [
      {
        locale: 'en',
        img: require('@/assets/images/flags/en.svg'),
        name: 'English',
      },
      {
        locale: 'da',
        img: require('@/assets/images/flags/dk.svg'),
        name: 'Dansk',
      },
    ]
    /* eslint-enable global-require */

    return {
      navMenuItems,
      perfectScrollbarSettings,
      shallShadowBottom,
      appName,
      appLogoImage,
      locales,
    }
  },
  computed: {
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale) || this.locales[1]
    },
  },
  created() {
    this.getNavItems()
  },
  methods: {
    async getNavItems() {
      this.navItems = await navMenuItems()
    },
    toggleLocale() {
      const newLocale = this.$i18n.locale === 'da' ? 'en' : 'da'
      this.$i18n.locale = newLocale
      localStorage.setItem('locale', newLocale)
    },
    logout() {
      this.$i18n.locale = 'da'
      // Remove tokens from localStorage
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      if (localStorage.getItem('locale')) localStorage.removeItem('locale')
      // Remove userData from localStorage
      localStorage.removeItem('userData')

      // Reset ability
      this.$ability.update(initialAbility)

      // Redirect to login page
      this.$router.push({ name: 'login' })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
